import { format, parseISO } from 'date-fns';

// ============================================================================
// CURRENCY FORMATTERS
// ============================================================================

/**
 * Format number as Indian Rupee currency. Default shows 2 decimals
 * (accounting standard). Pass more decimals when you need to reveal the
 * 7-decimal internal precision for charges breakdowns.
 */
export function formatCurrency(value: number | undefined | null, decimals: number = 2): string {
  if (value === undefined || value === null) return '-';

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format currency with decimals
 */
export function formatCurrencyPrecise(value: number | undefined | null): string {
  if (value === undefined || value === null) return '-';

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Full-precision currency formatter — shows up to 4 decimal places when the
 * value has sub-rupee precision. Use for charges and P&L displays where the
 * 7-decimal internal precision should be visible without drowning the UI in
 * trailing zeros.
 *
 * Examples:
 *   1715.232     -> "₹1,715.2320"
 *   4600         -> "₹4,600.0000"
 *   989.2088     -> "₹989.2088"
 */
export function formatCurrency7(value: number | undefined | null, decimals: number = 4): string {
  if (value === undefined || value === null) return '-';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format large numbers in Indian numbering system (Lakh, Crore) with ₹ symbol
 */
export function formatIndianNumber(value: number | undefined | null): string {
  if (value === undefined || value === null) return '₹0';

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absValue >= 10000000) {
    return `${sign}₹${(absValue / 10000000).toFixed(2)} Cr`;
  }
  if (absValue >= 100000) {
    return `${sign}₹${(absValue / 100000).toFixed(2)} L`;
  }
  if (absValue >= 1000) {
    return `${sign}₹${(absValue / 1000).toFixed(2)} K`;
  }
  return `${sign}₹${absValue.toFixed(0)}`;
}

/**
 * Format number with Indian commas and ₹ symbol (full number, no abbreviation)
 */
export function formatIndianCurrency(value: number | undefined | null, decimals: number = 2): string {
  if (value === undefined || value === null) return '₹0.00';

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

// ============================================================================
// NUMBER FORMATTERS
// ============================================================================

/**
 * Format number with commas
 */
export function formatNumber(value: number | undefined | null): string {
  if (value === undefined || value === null) return '-';

  return new Intl.NumberFormat('en-IN').format(value);
}

/**
 * Format percentage
 */
export function formatPercentage(value: number | undefined | null): string {
  if (value === undefined || value === null) return '-';

  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

/**
 * Format percentage without sign
 */
export function formatPercentageNoSign(value: number | undefined | null): string {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(2)}%`;
}

// ============================================================================
// DATE FORMATTERS
// ============================================================================

/**
 * Format date for display
 */
export function formatDate(dateString: string | undefined | null): string {
  if (!dateString) return '-';

  try {
    const date = parseISO(dateString);
    return format(date, 'dd MMM yyyy');
  } catch {
    return dateString;
  }
}

/**
 * Format date with time
 */
export function formatDateTime(dateString: string | undefined | null): string {
  if (!dateString) return '-';

  try {
    const date = parseISO(dateString);
    return format(date, 'dd MMM yyyy HH:mm');
  } catch {
    return dateString;
  }
}

/**
 * Format date for input fields
 */
export function formatDateForInput(dateString: string | undefined | null): string {
  if (!dateString) return '';

  try {
    const date = parseISO(dateString);
    return format(date, 'yyyy-MM-dd');
  } catch {
    return dateString;
  }
}

/**
 * Convert any ISO timestamp to its IST calendar date (YYYY-MM-DD).
 * Use this whenever bucketing trades into days — UTC `toISOString().slice(0,10)`
 * gives the wrong day between 00:00 and 05:30 IST.
 */
export function istDay(iso: string | undefined | null): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  return new Date(t + 5.5 * 60 * 60 * 1000).toISOString().substring(0, 10);
}

/**
 * Get today's IST date in yyyy-MM-dd format.
 */
export function getTodayDate(): string {
  return istDay(new Date().toISOString());
}

/**
 * Pick the canonical "bucket date" for a trade: closed trades by sale date
 * (when revenue was realised), open trades by buy date (when position opened).
 * Returns YYYY-MM-DD in IST. Use this on every "today/period" filter so
 * Dashboard, Analytics, TradesList, TradingDashboard, and ClientDetail all
 * agree on which day a trade belongs to.
 */
export function tradeBucketIstDay(t: { status?: string; saleDate?: string; date?: string; createdAt?: string }): string {
  const isClosed = t.status === 'CLOSED';
  const src = isClosed ? (t.saleDate || t.date || t.createdAt) : (t.date || t.createdAt);
  return istDay(src || '');
}

// ============================================================================
// P&L FORMATTERS
// ============================================================================

// Shared P&L colour palette (Tailwind-aligned). Use these constants on every
// page so the same number renders the same way everywhere.
export const PNL_COLORS = {
  positive: '#10B981',  // green
  warning: '#F59E0B',   // orange (for −10% to 0% band)
  negative: '#EF4444',  // red
  neutral: '#6B7280',   // grey
} as const;

/**
 * Format P&L value with color indicator
 */
export function formatPnL(value: number | undefined | null): { text: string; color: string } {
  if (value === undefined || value === null) {
    return { text: '-', color: 'inherit' };
  }

  const text = formatCurrency(value);
  const color = value >= 0 ? PNL_COLORS.positive : PNL_COLORS.negative;

  return { text, color };
}

/**
 * Format P&L percentage with color (3-tier: positive / -10% to 0% warning / <-10% loss)
 */
export function formatPnLPercentage(value: number | undefined | null): { text: string; color: string } {
  if (value === undefined || value === null) {
    return { text: '-', color: 'inherit' };
  }

  const text = formatPercentage(value);
  let color: string = PNL_COLORS.warning; // -10% ≤ value ≤ 0

  if (value > 0) {
    color = PNL_COLORS.positive;
  } else if (value < -10) {
    color = PNL_COLORS.negative;
  }

  return { text, color };
}

// ============================================================================
// RABBIT SCORE FORMATTERS
// ============================================================================

/**
 * Format RABBIT SCORE
 */
export function formatRabbitScore(score: number | undefined | null): string {
  if (score === undefined || score === null) return '-';
  return score.toFixed(2);
}

/**
 * Get RABBIT SCORE color
 */
export function getRabbitScoreColor(score: number): string {
  if (score >= 2.0) return PNL_COLORS.positive;
  if (score >= 1.0) return PNL_COLORS.warning;
  return PNL_COLORS.negative;
}

/**
 * Get RABBIT SCORE status text
 */
export function getRabbitScoreStatus(score: number): string {
  if (score >= 5.0) return 'Excellent';
  if (score >= 2.0) return 'Good';
  if (score >= 1.0) return 'Average';
  if (score >= 0.5) return 'Below Average';
  return 'Poor';
}

// ============================================================================
// STATUS FORMATTERS
// ============================================================================

/**
 * Get status color
 */
export function getStatusColor(status: 'healthy' | 'warning' | 'critical' | string): string {
  switch (status) {
    case 'healthy':
      return PNL_COLORS.positive;
    case 'warning':
      return PNL_COLORS.warning;
    case 'critical':
      return PNL_COLORS.negative;
    default:
      return PNL_COLORS.neutral;
  }
}

/**
 * Get trade status color
 */
export function getTradeStatusColor(status: string): string {
  switch (status) {
    case 'OPEN':
      return '#1976d2';
    case 'CLOSED':
      return '#9e9e9e';
    default:
      return '#9e9e9e';
  }
}

/**
 * Get alert severity color
 */
export function getAlertSeverityColor(severity: string): string {
  switch (severity) {
    case 'critical':
      return PNL_COLORS.negative;
    case 'high':
      return '#F97316'; // orange-500
    case 'medium':
      return PNL_COLORS.warning;
    case 'low':
      return '#3B82F6'; // blue-500
    default:
      return PNL_COLORS.neutral;
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
}

/**
 * Capitalize first letter
 */
export function capitalize(text: string): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Title Case — capitalize first letter of every word
 * "ajay singh" → "Ajay Singh", "RITU" → "Ritu", "md kashif" → "Md Kashif"
 */
export function toTitleCase(text: string): string {
  if (!text) return '';
  return text.replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

/**
 * Format user role for display
 */
export function formatRole(role: string): string {
  return capitalize(role);
}
